import { IModuleProps } from '../../core/utils/modules';

import { CountryField } from '../../document/components/AddressField';
import React from 'react';
import { ArticleNumberField } from '../../document/components/ArticleNumberField.tsx';
import {
    AssistanceFieldContextProvider,
    useAssistanceFieldContext,
} from '../../document/pages/Assistance/AssistanceFieldContext.tsx';
import { useArticleFormContext } from '../../products/list_of_services/components/PositionArticleSetCard.tsx';
import { SiemensArticleNumberFieldWrapper } from './SiemensArticleNumberFieldWrapper.tsx';
import { SiemensComponentFieldWrapper } from './SiemensComponentFieldWrapper.tsx';
import { FieldProps } from '../../document/pages/Assistance/customizable/Field';

const SCHIENENSYSTEME_PARTITION_ID = 'SCHIENENSYSTEME';

const SiemensListOfServicesArticleNumberField = React.memo((props: any) => {
    const assistanceFieldContext = useAssistanceFieldContext();

    return (
        <AssistanceFieldContextProvider
            {...assistanceFieldContext}
            // 'main' partition needs to be empty as free text search always also looks into main partition
            config={{
                ...assistanceFieldContext.config,
                partitionId: SCHIENENSYSTEME_PARTITION_ID,
                resetInvalidOnBlur: true,
            }}
        >
            <SiemensArticleNumberFieldWrapper {...props} />
        </AssistanceFieldContextProvider>
    );
});

const SiemensListOfServicesArticleNumber2Field = React.memo((props: any) => {
    const { fields } = useArticleFormContext();
    const assistanceFieldContext = useAssistanceFieldContext();

    const articleNumberField = fields.find((f) => f.fieldName === 'number');
    const articleNumber = articleNumberField?.value;

    return (
        <AssistanceFieldContextProvider
            {...assistanceFieldContext}
            // inject the article number 1 as partition id
            config={{
                ...assistanceFieldContext.config,
                partitionId: articleNumber,
                searchLookupOnEmptyValue: true,
                resetInvalidOnBlur: true,
            }}
        >
            <ArticleNumberField {...props} />
        </AssistanceFieldContextProvider>
    );
});

const SiemensListOfServicesComponentField = React.memo(function SiemensListOfServicesComponentField(props: FieldProps) {
    const { fields, article } = useArticleFormContext();
    const assistanceFieldContext = useAssistanceFieldContext();
    const articleNumber = fields.find((f) => f.fieldName === 'number')?.value;
    const articleNumber3 = article?.articleNumber3?.value;
    const partitionId = articleNumber3 || articleNumber;

    return (
        <AssistanceFieldContextProvider
            {...assistanceFieldContext}
            // There are in general two ways to lookup components depending on the rail system.
            // Some rail systems have a GLOBAL flag within article_id_3, that means they can search all components.
            // Alternatively, only we can only search components, that have article_id_1 as their partition_id.
            // That's why partition_id is primarily articleNumber3 or article_id_1 otherwise.
            config={{
                ...assistanceFieldContext.config,
                partitionId,
                allowInvalidIds: false,
                resetInvalidOnBlur: true,
            }}
        >
            <SiemensComponentFieldWrapper {...props} />
        </AssistanceFieldContextProvider>
    );
});

const SiemensListOfServicesComponentDescriptionField = React.memo(
    function SiemensListOfServicesComponentDescriptionField(props: FieldProps) {
        const { fields, article } = useArticleFormContext();
        const assistanceFieldContext = useAssistanceFieldContext();
        const articleNumber = fields.find((f) => f.fieldName === 'number')?.value;
        const articleNumber3 = article?.articleNumber3?.value;
        const partitionId = articleNumber3 || articleNumber;

        // Use the lookup options from the description field config
        const descriptionField = fields.find((f) => f.fieldName === 'description');
        const descriptionFieldConfig = descriptionField?.config;

        return (
            <AssistanceFieldContextProvider
                {...assistanceFieldContext}
                config={{
                    ...descriptionFieldConfig,
                    name: assistanceFieldContext.fieldName,
                    partitionId,
                    allowInvalidIds: true,
                }}
            >
                <SiemensComponentFieldWrapper {...props} />
            </AssistanceFieldContextProvider>
        );
    }
);

export default {
    name: 'siemens_smart_infrastructure',
    components: {
        Field: [
            {
                condition: ({ fieldName }) => fieldName === 'end_customer__country',
                component: CountryField,
            },
            {
                condition: ({ fieldName, documentTypeName }) =>
                    documentTypeName === 'listOfServices' && fieldName === 'number',
                component: SiemensListOfServicesArticleNumberField,
            },
            {
                condition: ({ fieldName, documentTypeName }) =>
                    documentTypeName === 'listOfServices' && fieldName === 'article_number_2',
                component: SiemensListOfServicesArticleNumber2Field,
            },
            {
                condition: ({ fieldName, documentTypeName }) =>
                    documentTypeName === 'listOfServices' && fieldName === 'article_partition_specific_number',
                component: SiemensListOfServicesComponentField,
            },
            {
                condition: ({ fieldName, documentTypeName }) =>
                    documentTypeName === 'listOfServices' && fieldName === 'description',
                component: SiemensListOfServicesArticleNumberField,
            },
            {
                condition: ({ fieldName, documentTypeName }) =>
                    documentTypeName === 'listOfServices' && fieldName === 'short_code',
                component: SiemensListOfServicesComponentDescriptionField,
            },
        ],
    },
    featureFlags: {
        highlightSelectedInDocumentSuggestions: true,
    },
} as IModuleProps;
